import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import AssistantAttachment from "ai-assistant/attachments/assistant-attachment";
import AssistantCitation from "ai-assistant/citations/assistant-citation";
import AssistantCitationSchema from "ai-assistant/citations/assistant-citation-schema";
import AnswerFeedback from "ai-assistant/feedback/answer-feedback";
import AssistantGroundingSchema from "ai-assistant/grounding/assistant-grounding-schema";
import AssistantResultTable from "ai-assistant/result-tables/assistant-result-table";
import AssistantResultTableSchema from "ai-assistant/result-tables/assistant-result-table-schema";
import AssistantTurnStep from "ai-assistant/turn-steps/assistant-turn-step";
import AssistantTurnStepSchema from "ai-assistant/turn-steps/assistant-turn-step-schema";
import StagedActionSchema from "ai-assistant/staged-actions/staged-action-schema";
import { Role } from "ai-assistant/roles/assistant-role";
import { AssistantResponseStatus } from "ai-assistant/statuses/assistant-response-status";
import { z } from "zod";

export type AssistantResponseJSON = z.infer<typeof AssistantResponseSchema.AssistantResponseJSONZ>;
export type AssistantResponseParams = z.infer<typeof AssistantResponseSchema.AssistantResponseZ>;

export default class AssistantResponseSchema {
  public static readonly AssistantResponseJSONZ = z.object({
    conversationId: z.uuid().nullish(),
    role: z.enum(Role),
    text: z.string(),
    citations: z.array(AssistantCitationSchema.AssistantCitationJSONZ),
    timestamp: z.iso.datetime({ offset: true }),
    // Absent on a reply from an API that predates the field; absent, not a failure.
    status: z.enum(AssistantResponseStatus).nullish(),
    errorCode: z.string().nullish(),
    // The trace the turn ran under, on every reply a turn produced - failed ones included. What
    // a report about this answer quotes back. Absent on a reply no turn produced, and on an API
    // that predates the field.
    traceId: z.string().nullish(),
    // Omitted entirely when the turn prepared nothing, which is every turn until a write
    // capability is switched on. A prepared action is still an Answered reply - its
    // presence here is the signal, not the status.
    stagedActions: z.array(StagedActionSchema.StagedActionJSONZ).nullish(),
    // Absent on a failed or conflicted turn, where nothing was verified, and on a reply from
    // an API that predates the field. Absent is read as NotApplicable rather than as
    // Grounded: an unchecked answer must not arrive looking like a checked one.
    grounding: AssistantGroundingSchema.AssistantGroundingJSONZ.nullish(),
    // Tables the turn's lookups answered with, drawn as cards under the text (#6223). Omitted
    // when there is none, which is most turns, and by an API that predates the field.
    resultTables: z.array(AssistantResultTableSchema.AssistantResultTableJSONZ).nullish(),
    // What the turn did on the way to its answer, listed beneath it (#7284). Omitted on a reply no
    // recorded turn produced - a failure, a conflict - and by an API that predates the field.
    steps: z.array(AssistantTurnStepSchema.AssistantTurnStepJSONZ).nullish(),
  });

  public static readonly AssistantResponseZ = z.object({
    conversationId: z.string().optional(),
    role: z.enum(Role),
    text: z.string(),
    // Instances, not params: a citation is validated once, where its JSON is decoded, and a
    // response built from citations that already exist takes them as they are (#5992).
    citations: z.array(z.instanceof(AssistantCitation)),
    timestamp: z.custom<AHDate>(),
    // Optional so a locally-built message - the user's own turn, an optimistic echo -
    // does not have to state a status it has no opinion about. It defaults to Answered.
    status: z.enum(AssistantResponseStatus).optional(),
    errorCode: z.string().optional(),
    traceId: z.string().optional(),
    stagedActions: z.array(StagedActionSchema.StagedActionZ).optional(),
    grounding: AssistantGroundingSchema.AssistantGroundingZ.optional(),
    resultTables: z.array(z.instanceof(AssistantResultTable)).optional(),
    steps: z.array(z.instanceof(AssistantTurnStep)).optional(),
    // Client-side only, and deliberately absent from the JSON shape above: the server sends
    // prepared actions or it does not, and has no way to say "there were some and you are not
    // getting them". That sentence belongs to a transcript being replayed, so it is set when
    // one is built rather than received. See `AssistantConversation.toTranscript`.
    preparedActionsNotReplayed: z.boolean().optional(),
    // Client-side only for the same reason: a replayed transcript says it, a live reply never
    // does. See `AssistantConversation.toTranscript`.
    sourceLost: z.boolean().optional(),
    // Client-side only, for the same reason: a live reply has not been reported yet, and a
    // replayed one carries the report from the conversation read. See `toTranscript`.
    feedback: z.instanceof(AnswerFeedback).optional(),
    // Client-side only, for the same reason as `preparedActionsNotReplayed`: a replayed answer
    // that was shown with a result table arrives without it. See `toTranscript`.
    resultTablesNotReplayed: z.boolean().optional(),
    // Client-side only: the files the user's own message carried (#6504). A reply from the
    // server is the assistant's, which carries none; the user's message is built here, from what
    // the composer uploaded or from a replayed turn. See `AssistantConversation.toTranscript`.
    attachments: z.array(z.instanceof(AssistantAttachment)).optional(),
  });

  public static validate(values: unknown): AssistantResponseParams {
    return parseOrThrow(
      AssistantResponseSchema.AssistantResponseZ, 
      values, 
      { 
        logError: true,
        schemaName: "AssistantResponseSchema",
        operation: "validate",
        payloadSample: values 
      }
    );
  }

  public static validateJSON(json: unknown): AssistantResponseJSON {
    return parseOrThrow(
      AssistantResponseSchema.AssistantResponseJSONZ, 
      json, 
      { 
        logError: true,
        schemaName: "AssistantResponseSchema",
        operation: "validateJSON",
        payloadSample: json 
      }
    );
  }

  public static isValid(values: unknown): values is AssistantResponseParams {
    try {
      AssistantResponseSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is AssistantResponseJSON {
    try {
      AssistantResponseSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  /** Validates `json` once and decodes it. Parsing through the codec is the one parse. */
  public static deserialize(json: AssistantResponseJSON): AssistantResponseParams {
    if (isNullOrUndefined(json)) return json;
    return parseOrThrow(
      AssistantResponseCodec,
      json,
      {
        logError: true,
        schemaName: "AssistantResponseSchema",
        operation: "deserialize",
        payloadSample: json
      }
    );
  }

  public static serialize(legalEntity: AssistantResponseParams): AssistantResponseJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = AssistantResponseSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return AssistantResponseCodec.encode(parsed);
  }
}

/**
 * The response JSON and its params, as one schema. Parsing JSON through it validates the JSON once
 * and decodes it. It is the endpoints' `rawResponseSchema`, so a live turn's reply is validated where
 * it arrives and handed to the constructor already decoded.
 */
export const AssistantResponseCodec = z.codec(
  AssistantResponseSchema.AssistantResponseJSONZ,
  AssistantResponseSchema.AssistantResponseZ,
  {
    decode: (data) => ({
      conversationId: data.conversationId ?? undefined,
      role: data.role as Role,
      text: data.text,
      // Validated as part of this response's parse, so decoded without a second one.
      citations: data.citations.map((citation) => new AssistantCitation(AssistantCitationSchema.decodeParsed(citation))),
      timestamp: new AHDate(data.timestamp),
      // Answered is 0, so `?? Answered` rather than `|| Answered`: the nullish
      // coalescing is what keeps a real, answered reply from being re-defaulted.
      status: data.status ?? AssistantResponseStatus.Answered,
      errorCode: data.errorCode ?? undefined,
      traceId: data.traceId ?? undefined,
      stagedActions: (data.stagedActions ?? []).map(
        (action) => StagedActionSchema.deserialize(action)
      ),
      grounding: isNullOrUndefined(data.grounding)
        ? undefined
        : AssistantGroundingSchema.deserialize(data.grounding),
      // Validated as part of this response's parse, so decoded without a second one.
      resultTables: (data.resultTables ?? []).map(
        (table) => new AssistantResultTable(AssistantResultTableSchema.decodeParsed(table))
      ),
      // Validated as part of this response's parse, so decoded without a second one.
      steps: (data.steps ?? []).map((step) => new AssistantTurnStep(AssistantTurnStepSchema.decodeParsed(step))),
      preparedActionsNotReplayed: undefined,
      sourceLost: undefined,
      feedback: undefined,
      resultTablesNotReplayed: undefined,
      attachments: undefined,
    }),
    encode: (response) => ({
      conversationId: response.conversationId,
      role: response.role,
      text: response.text,
      citations: response.citations.map((citation) => AssistantCitationSchema.serialize(citation)),
      timestamp: response.timestamp.toISOString(),
      status: response.status,
      errorCode: response.errorCode,
      traceId: response.traceId,
      stagedActions: (response.stagedActions ?? []).map(
        (action) => StagedActionSchema.serialize(action)
      ),
      grounding: isNullOrUndefined(response.grounding)
        ? undefined
        : AssistantGroundingSchema.serialize(response.grounding),
      resultTables: (response.resultTables ?? []).map((table) => AssistantResultTableSchema.serialize(table)),
      steps: (response.steps ?? []).map((step) => AssistantTurnStepSchema.serialize(step)),
    }),
  }
);
